import { type JanusCtx, type Row, ecId, ecLean, actorId, inventoryId, handBack, record, upper } from './context.js';
import { sizeIdOf, inventoryField, posCoord } from '../../shared/array-types.js';
import { readInventoryRows } from '../compile/inventory-raw-readers.js';
import { getSwarmTemplateForCreature, swarmOccupiedWidthFt, swarmVRatio01, swarmWidthLanesForCreature } from '../../shared/swarm-templates.js';

/**
 * ranged_stray_shot_helpers.php — a ranged natural 1 retargets onto ONE token on the line of fire (or, failing that, within 5 ft of the
 * intended target), picked with odds weighted by footprint area.
 * OWNER RULING 2026-10-15 (Guiding Bolt playtest): the line of fire is drawn on the FT3D GROUND PLANE (x / y — z is elevation), over each
 * token's base footprint. PHP's legacy XZ widget math ignored y, so on an FT3D map every token whose x fell between caster and
 * target "stood on the line" (35 candidates, trees 370 ft away) — a browser rule now, deliberately not legacy parity.
 */

// SizeType::LIST base_size (additions.php); an unknown id is Medium (get_size_dimensions()).
const SIZE_BASE_FT: Readonly<Record<string, number>> = {
  '13456FEC-A2EE-DB46-5829-0EB2F627E1B7': 2.5, 'AAC1F466-7A8B-C593-1ADC-FC402B8909D7': 3.75,
  'DC23AA88-CB3D-EA4E-F6F7-6389ABC4F9DC': 5, '52FFDB16-A991-C8D4-FE59-7C75EE6CF7AD': 10,
  'D0EF6D7A-82BC-1E6E-9D36-5004C7E0561C': 15, 'CEA2946E-D395-46EA-A6E7-74D21EA2376E': 20,
  'C94CDD74-8ACD-26B0-5C53-4CA29CF21D68': 25, '4C33FD22-5382-15DA-1C46-F57F14378AFD': 30,
};
export interface Rect { left: number; right: number; bottom: number; top: number }

/** segment_axis_aligned_rect_first_intersection_t() — verbatim slab test. */
export function segmentRectFirstT(x0: number, z0: number, x1: number, z1: number, rect: Rect, eps = 1e-5): number | null {
  const dx = x1 - x0, dz = z1 - z0;
  let tMin = 0, tMax = 1;
  if (Math.abs(dx) < eps) { if (x0 < rect.left || x0 > rect.right) return null; }
  else {
    const inv = 1 / dx; let t1 = (rect.left - x0) * inv, t2 = (rect.right - x0) * inv;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tMin = Math.max(tMin, t1); tMax = Math.min(tMax, t2);
    if (tMin > tMax + eps) return null;
  }
  if (Math.abs(dz) < eps) { if (z0 < rect.bottom || z0 > rect.top) return null; }
  else {
    const inv = 1 / dz; let t1 = (rect.bottom - z0) * inv, t2 = (rect.top - z0) * inv;
    if (t1 > t2) [t1, t2] = [t2, t1];
    tMin = Math.max(tMin, t1); tMax = Math.min(tMax, t2);
    if (tMin > tMax + eps) return null;
  }
  const lo = Math.max(0, tMin), hi = Math.min(1, tMax);
  if (lo > hi + eps) return null;
  const start = Math.max(lo, eps), end = Math.min(hi, 1 - eps);
  if (start > end + eps) return null;
  return start;
}

/** A token's base footprint on the FT3D ground plane (x / y); swarms widen x, retaining individual depth. */
export function groundFootprint(row: Row): Rect | null {
  const center = groundCenter(row);
  if (!center) return null;
  const [x, y] = center;
  const sizeId = upper(inventoryField(row, 'inventory_size') || sizeIdOf(row) || '');
  const base = sizeId ? (SIZE_BASE_FT[sizeId] ?? 5) : 0;
  const swarm = getSwarmTemplateForCreature(row as never);
  const width = swarm ? swarmOccupiedWidthFt(base,
    { ...swarm, monsterQty: Number(ecLean(row, 'swarm_members_start') ?? swarm.monsterQty) },
    swarmWidthLanesForCreature(row as never, swarm), swarmVRatio01(row as never)) : base;
  return { left: x - width / 2, right: x + width / 2, bottom: y - base / 2, top: y + base / 2 };
}
function groundCenter(row: Row): [number, number] | null {
  if (!actorId(row)) return null;
  // build_pos_arr() elides zero axes (including the whole position at the origin) on persisted actors.
  const position = record(row.position) ?? record(row.pos_arr)
    ?? (('x_pos' in row || 'y_pos' in row || 'z_pos' in row) ? row : null)
    ?? record(row.encounter_creatures) ?? record(row.inventory) ?? row;
  const x = posCoord(position, 'x_pos') ?? ('x_pos' in position ? NaN : 0);
  const y = posCoord(position, 'y_pos') ?? ('y_pos' in position ? NaN : 0);
  return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
}

function liveCandidate(row: Row, casterId: string, intendedId: string): boolean {
  const id = ecId(row);
  if (!id || id === casterId || id === intendedId) return false;
  if (Number(ecLean(row, 'encounter_creature_dead') ?? 0)) return false;
  if (Number(ecLean(row, 'encounter_creature_private') ?? 0)) return false;
  return true;
}
/** find_ranged_stray_shot_candidate()'s candidate set, in PHP's order (roster order), on STATE alone: the line-of-fire hits,
 * else the tokens within 5 ft of the intended target. Creatures precede floor inventories, as in PHP. */
export function strayCandidates(ctx: Pick<JanusCtx, 'encounter' | 'members'>, caster: Row, intended: Row): Row[] {
  const creatures = (Array.isArray(ctx.encounter.encounter_creatures_arr) ? ctx.encounter.encounter_creatures_arr : [])
    .map(record).filter((r): r is Row => !!r);
  const roster = ctx.encounter.encounter_inventories_arr;
  if (!Array.isArray(roster) && !record(roster)) handBack('find_ranged_stray_shot_candidate:inventory-roster-not-held');
  const inventories = readInventoryRows(ctx.encounter, 'encounter_inventories_arr');
  if (!creatures.length && !inventories.length) return [];
  const from = groundCenter(caster), to = groundCenter(intended);
  if (!from || !to) handBack(`find_ranged_stray_shot_candidate:${from ? 'target' : 'caster'}-position-not-held`);
  const [x0, y0] = from!, [x1, y1] = to!;
  if (Math.hypot(x1 - x0, y1 - y0) < 1e-6) return [];
  const casterId = ecId(caster), intendedId = ecId(intended);
  const live = [...creatures.filter(row => liveCandidate(row, casterId, intendedId)),
    ...inventories.map(record).filter((row): row is Row => !!row && !!inventoryId(row) && inventoryId(row) !== inventoryId(intended))];
  // The roster's own row for a member is the page copy; the resolver reads its live clone where one exists.
  const current = (row: Row): Row => ctx.members.get(ecId(row)) ?? row;
  const placed = live.map(current).flatMap((row) => { const rect = groundFootprint(row); return rect ? [{ row, rect }] : []; });
  const lineHits = placed.filter(({ rect }) => segmentRectFirstT(x0, y0, x1, y1, rect) !== null);
  if (lineHits.length) return lineHits.map(({ row }) => row);
  return placed.filter(({ rect }) => Math.hypot((rect.left + rect.right) / 2 - x1, (rect.bottom + rect.top) / 2 - y1) <= 5 + 1e-6)
    .map(({ row }) => row);
}

/** pick_ranged_stray_shot_row_weighted_by_footprint(): ONE uniform 1..total draw (ft_rng_die), weight = ceil(max(area,.25)*100). */
export function pickStrayCandidate(ctx: Pick<JanusCtx, 'roll' | 'strayProbe'>, candidates: Row[]): Row {
  const weights = candidates.map(row => {
    const r = groundFootprint(row);
    if (!r) handBack('pick_ranged_stray_shot_row:position-not-held');
    const area = Math.max(0, (r!.right - r!.left) * (r!.top - r!.bottom));
    return Math.max(1, Math.ceil(Math.max(area, 0.25) * 100));
  });
  const total = weights.reduce((sum, w) => sum + w, 0);
  const draw = ctx.roll.d(total);
  if (ctx.strayProbe) {
    const forced = candidates.find((row) => actorId(row) === ctx.strayProbe!.candidateId);
    ctx.strayProbe.picked();
    if (forced) return forced;
  }
  let cumulative = 0;
  for (let i = 0; i < candidates.length; i++) { cumulative += weights[i]!; if (draw <= cumulative) return candidates[i]!; }
  return candidates[candidates.length - 1]!;
}
